import Box from "@/main/components/shared/box";
import Button from "@/main/components/shared/button/button";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import { AreaButton } from "@/main/components/shared/styles";
import useFormQueryParams from "@/main/hooks/useformQueryParams";
import { KeyValue } from "@/main/models/common";
import { RoutesConstants } from "@/main/routes/routesConstants";
import {
	approvalMatrixFormFilterSchema,
	IApprovalMatrixFilterForm,
	IApprovalMatrixFilterFormOutput,
	INITIAL_APPROVAL_MATRIX_FILTER_FORM,
} from "@/main/screens/registration/approvalMatrix/filtersApprovalMatrix/utilsFilterApprovalMatrix";
import { getOptionsStatus } from "@/main/services/data/data";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import { isNullUndefined } from "@/main/services/reducerUtils";
import {
	getDefaultCountryOptionDrop,
	getDefaultOptionDrop,
	getTranslateByKey,
	isNullUndefinedEmpty,
	mountReducerWithData,
} from "@/main/services/utils";
import { userIsInsurer } from "@/main/services/utilsUser";
import { getApprovalMatrixType, useApprovalMatrixTypeReducer } from "@/main/store/approvalMatrixType";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getInsurers, useInsurerReducer } from "@/main/store/insurer";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as S from "./styles";
const ID_BASE_COMPONENTS: string = "id-searchApprovalMatrix";

interface IFiltersApprovalMatrix {
	onSubmit: (data: IApprovalMatrixFilterFormOutput) => void;
	onClear: () => void;
}

export default function FiltersApprovalMatrix({ onClear, onSubmit }: Readonly<IFiltersApprovalMatrix>) {
	const [showFilters, setShowFilters] = useState<boolean>(true);

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const countryReducer = useCountryReducer();
	const {
		countriesUser: { data: countries, isLoading: isLoadingCountries },
	} = countryReducer;
	const insurerReducer = useInsurerReducer();
	const {
		insurers: { data: insurers, isLoading: isLoadingInsurers },
	} = insurerReducer;
	const approvalMatrixTypeReducer = useApprovalMatrixTypeReducer();
	const {
		approvalMatrixType: { data: approvalMatrixTypes, isLoading: isLoadingApprovalMatrixType },
	} = approvalMatrixTypeReducer;

	const statusRegisterOptions = getOptionsStatus();
	const defaultStatusOption =
		statusRegisterOptions.find((option) => Number(option.key) === StatusRegisterEnum.Active) || null;

	const optionDefaultCountryDrop = getDefaultCountryOptionDrop(countries);
	const optionDefaultInsurerDrop = getDefaultOptionDrop(insurers);

	const isDisabledInsurerDrop = userIsInsurer();

	function callInsurerApiDomain(guidCountry: string) {
		dispatch(getInsurers({ guidCountry }));
	}

	function handleNew() {
		navigate(RoutesConstants.registrationRegisterApprovalMatrix);
	}

	function handleClear() {
		reset(INITIAL_APPROVAL_MATRIX_FILTER_FORM);

		if (!isNullUndefined(optionDefaultCountryDrop)) {
			setValue("country", optionDefaultCountryDrop);

			if (!isDisabledInsurerDrop) {
				callInsurerApiDomain(String(optionDefaultCountryDrop.key));
			}
		}

		if (defaultStatusOption) {
			setValue("statusRegister", defaultStatusOption);
		}

		if (!isNullUndefinedEmpty(optionDefaultInsurerDrop)) {
			setValue("insurer", optionDefaultInsurerDrop);
		}

		onClear();
	}

	useEffect(() => {
		dispatch(getCountriesUser());
		dispatch(getApprovalMatrixType());
	}, []);

	useEffect(() => {
		if (!isNullUndefined(optionDefaultCountryDrop?.key)) {
			callInsurerApiDomain(String(optionDefaultCountryDrop.key));
		}
	}, [optionDefaultCountryDrop?.key]);

	const {
		control,
		setValue,
		customHandleSubmit,
		reset,
		formState: { errors },
	} = useFormQueryParams<IApprovalMatrixFilterForm, IApprovalMatrixFilterFormOutput>({
		mode: "onChange",
		defaultValues: INITIAL_APPROVAL_MATRIX_FILTER_FORM,
		resolver: zodResolver(approvalMatrixFormFilterSchema),
		onSubmit: onSubmit,
		mapDefaultValues: {
			country: countryReducer.countriesUser,
			insurer: insurerReducer.insurers,
			approvalMatrixType: approvalMatrixTypeReducer.approvalMatrixType,
			statusRegister: mountReducerWithData(statusRegisterOptions),
		},
	});
	return (
		<>
			<AreaButton>
				<Button id={`${ID_BASE_COMPONENTS}-btn-filters`} onClick={() => setShowFilters((prev) => !prev)}>
					{getTranslateByKey("information.filters")}
				</Button>

				<Button id={`${ID_BASE_COMPONENTS}-btn-register`} onClick={() => handleNew()}>
					{getTranslateByKey("information.newApprovalMatrix")}
				</Button>
			</AreaButton>

			<Box showBox={showFilters} title={getTranslateByKey("information.filters")}>
				<form onSubmit={customHandleSubmit} noValidate>
					<S.RowInputs>
						<KogAutoCompleteControlled
							required
							id={`${ID_BASE_COMPONENTS}-select-country`}
							label={getTranslateByKey("information.country")}
							control={control}
							options={countries}
							isLoading={isLoadingCountries}
							nameCustom="country"
							rulesCustom={{ required: true }}
							errorMessageCustom={errors.country?.message ?? ""}
							hasErrorCustom={!!errors.country}
							onChangeCustom={(optionSelected: KeyValue | null) => {
								if (optionSelected) callInsurerApiDomain(String(optionSelected.key));
							}}
							valueDefaultKey={optionDefaultCountryDrop?.key}
							setValueCustom={setValue}
							disabled={optionDefaultCountryDrop !== null}
						/>

						<KogAutoCompleteControlled
							id={`${ID_BASE_COMPONENTS}-select-insurer`}
							label={getTranslateByKey("information.insurer")}
							control={control}
							options={insurers}
							isLoading={isLoadingInsurers}
							nameCustom="insurer"
							setValueCustom={setValue}
							isDefaultValueUnique
							disabled={isDisabledInsurerDrop}
							valueDefaultKey={optionDefaultInsurerDrop?.key}
							onChangeCustom={() => {}}
						/>

						<KogInputTextControlledNew
							control={control}
							name="matrixName"
							id={`${ID_BASE_COMPONENTS}-text-matrixName`}
							label={getTranslateByKey("information.matrixName")}
						/>

						<KogAutoCompleteControlled
							id={`${ID_BASE_COMPONENTS}-select-approvalMatrixType`}
							label={getTranslateByKey("information.approvalMatrixType")}
							control={control}
							options={approvalMatrixTypes}
							isLoading={isLoadingApprovalMatrixType}
							nameCustom="approvalMatrixType"
							setValueCustom={setValue}
							isDefaultValueUnique
							onChangeCustom={() => {}}
						/>

						<KogAutoCompleteControlled
							id={`${ID_BASE_COMPONENTS}-select-statusRegister`}
							label={getTranslateByKey("information.status")}
							nameCustom="statusRegister"
							control={control}
							setValueCustom={setValue}
							options={statusRegisterOptions}
							valueDefaultKey={defaultStatusOption?.key}
							onChangeCustom={() => {}}
						/>
					</S.RowInputs>
					<S.AreaButtons>
						<SecondaryButton id={`${ID_BASE_COMPONENTS}-btn-search`} type="submit">
							{getTranslateByKey("information.search")}
						</SecondaryButton>

						<SecondaryButton id={`${ID_BASE_COMPONENTS}-btn-clear`} onClick={handleClear}>
							{getTranslateByKey("information.clear")}
						</SecondaryButton>
					</S.AreaButtons>
				</form>
			</Box>
		</>
	);
}
